import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IApiError } from '@models/iapi';
import { AlertToastService, InternationalInvApiService } from '@services';
import { getError } from '@shared/utils/error.utils';
import { getStringLength, trimMaxLength, trimSpaces } from '@shared/utils/helpers.util';
import { Unsubscriber } from '@shared/classes/unsubscriber';
import { BehaviorSubject, distinctUntilChanged, filter, finalize, merge, take } from 'rxjs';
import { InvoiceRegisterApiService } from '../../../../services/registers/invoice-register.api.service';
import { IRegisterRenameComponentPayload } from './international-register-rename.models';
import {
  IIntInvoiceRegisterPrintPayload,
  IIntInvoiceRegisterUpdatePayload,
} from '../../../../models/invoice/int-invoice-register';
import { IntRegistersApiService } from '../../../../services/int-registers-api/int-registers-api.service';

const descriptionErrors = {
  required: 'Це поле обов\'язкове',
  maxlength: 'Максимальна довжина 30 символів',
  pattern: 'Містить недопустимий символ',
};

@Component({
  selector: 'app-register-rename',
  templateUrl: './international-register-rename.component.html',
  styleUrls: ['./international-register-rename.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternationalRegisterRenameComponent extends Unsubscriber implements OnInit {
  readonly pattern = /^[а-яА-Я,ёЁіІїЇєЄґҐюЮa-zA-Z\d\s\.№;!:/\-'\*\+#`',\/\(\)"«»\\\\_&„""]*$/ui;
  maxLength = 30;
  maxInputLength = 50;
  form: FormGroup | null = null;
  protected readonly descriptionErrors = descriptionErrors;
  protected readonly getError = getError;
  private _loading = new BehaviorSubject(false);
  loading$ = this._loading.asObservable();

  constructor(
    private intInvoiceRegisterService: IntRegistersApiService,
    private alertToastService: AlertToastService,
    @Inject(MAT_DIALOG_DATA) private data: IRegisterRenameComponentPayload,
    private dialogRef: MatDialogRef<InternationalRegisterRenameComponent>,
    private fb: FormBuilder,
  ) {
    super();
  }

  get registerNumber(): string {
    return (this.data.register as any).docNumber ?? '';
  }

  get description(): AbstractControl {
    return this.form?.get('description') as AbstractControl;
  }

  ngOnInit(): void {
    this.initForm();
    this.initCheckMaxStringLengthSubscription();
    this.subscribeToClose();
  }

  close(success?: boolean, description?: string): void {
    this.dialogRef.close({ success, description });
  }

  onBlur($event: FocusEvent): void {
    let value = ($event.target as HTMLInputElement).value;
    this.checkMaxStringLength(value, true);
  }

  clearField(): void {
    this.description?.setValue('');
  }

  confirm(): void {
    if (this.form.invalid) {
      this.description.markAsTouched();
      return;
    }

    if (this.description.value === this.data.register.description) {
      this.close();
      return;
    }

    this._loading.next(true);

    const renamedRegister: IIntInvoiceRegisterUpdatePayload = {
      Ref: this.data.register.ref,
      Description: this.description.value,
    };

    this.subscriptions = this.intInvoiceRegisterService.update(renamedRegister)
      .pipe(finalize(() => this._loading.next(false)))
      .subscribe(
        () => {
          this.close(true, this.description.value);
        },
        (errors: IApiError[]) => {
          if (errors[0]) {
            this.alertToastService.pushApiErrors(errors);
          }
        },
      );
  }

  private initForm(): void {
    this.form = this.fb.group({
      description: [this.data.register.description, [Validators.maxLength(30), Validators.required, Validators.pattern(this.pattern)]],
    });
  }

  private initCheckMaxStringLengthSubscription(): void {
    this.subscriptions = this.description?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value: string) => this.checkMaxStringLength(value));
  }

  private checkMaxStringLength(value: string, shouldTrimSpaces: boolean = false): void {
    if (!this.description) {
      return;
    }

    const cleanValue = shouldTrimSpaces ? trimSpaces(value) : value;
    if (getStringLength(trimSpaces(value)) > this.maxInputLength) {
      this.description?.setValue(trimMaxLength(trimSpaces(value), this.maxInputLength));
    } else {
      this.description?.setValue(cleanValue);
    }
  }

  private subscribeToClose(): void {
    const backdrop = this.dialogRef.backdropClick();
    const keyEvents = this.dialogRef.keydownEvents().pipe(filter((e) => e.key === 'Escape'));
    this.subscriptions = merge(backdrop, keyEvents).pipe(filter(() => !this._loading.value), take(1)).subscribe(() => this.close());
  }
}
